"use client";

import { useEffect, useState, useCallback } from "react";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";
import { PersonalEjecutivo } from "@/lib/api/datosGenerales/types/organizacion";
import { fetchOrganizacion } from "@/lib/api/datosGenerales/fetchOrganizacion";
import OrganizacionSkeleton from "./components/OrganizacionSkeleton";
import { type Locale } from "@/i18n/config";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Generales/321A0381.jpg";

function MemberCard({
  person,
  size = "default",
}: {
  person: PersonalEjecutivo;
  size?: "default" | "featured";
}) {
  const isFeatured = size === "featured";
  const avatarSize = isFeatured ? "w-32 h-32 md:w-36 md:h-36" : "w-24 h-24";

  return (
    <article className="group bg-white p-8 flex flex-col items-center text-center transition-colors hover:bg-gray-50">
      <div
        className={`relative ${avatarSize} rounded-full overflow-hidden bg-gray-100 mb-5 ring-1 ring-gray-200`}
      >
        <Image
          src={person.foto || "/default-profile.png"}
          alt={person.nombre}
          fill
          className="object-cover object-top"
          sizes={isFeatured ? "144px" : "96px"}
        />
      </div>
      <h3
        className={`font-semibold text-gray-900 tracking-tight ${isFeatured ? "text-lg md:text-xl" : "text-base"} leading-snug mb-1`}
      >
        {person.nombre}
      </h3>
      <p className="text-sm text-gray-500 leading-relaxed">{person.cargo}</p>
    </article>
  );
}

function SectionBlock({
  title,
  people,
  featured = false,
}: {
  title: string;
  people: PersonalEjecutivo[];
  featured?: boolean;
}) {
  if (people.length === 0) return null;

  const cols = featured
    ? "grid-cols-1"
    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  return (
    <section className="mb-16">
      <div className="flex items-end justify-between gap-2 mb-8">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold">
          <span className="w-8 h-px bg-[#102542]" />
          {title}
        </span>
      </div>
      <div
        className={`grid ${cols} gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200`}
      >
        {people.map((person) => (
          <MemberCard
            key={person.id}
            person={person}
            size={featured ? "featured" : "default"}
          />
        ))}
      </div>
    </section>
  );
}

function Hero({ t }: { t: ReturnType<typeof useTranslations> }) {
  return (
    <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
      <Image
        src={HERO_IMAGE}
        alt={t("title")}
        fill
        className="object-cover"
        priority
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
      <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
            <span className="w-8 h-px bg-white" />
            {t("eyebrow")}
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
            {t("title")}
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed">
            {t("subtitle")}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function EquipoPage() {
  const t = useTranslations("organizacionPage");
  const locale = useLocale() as Locale;

  const [personal, setPersonal] = useState<{
    gerente: PersonalEjecutivo[];
    vicepresidencias: PersonalEjecutivo[];
    gerencias: PersonalEjecutivo[];
  } | null>(null);
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );

  const loadData = useCallback(async () => {
    try {
      setStatus("loading");
      const res = await fetchOrganizacion(locale);
      setPersonal(res);
      setStatus("success");
    } catch (error) {
      console.error("Error al cargar organización:", error);
      setStatus("error");
    }
  }, [locale]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasData =
    personal &&
    (personal.gerente.length > 0 ||
      personal.vicepresidencias.length > 0 ||
      personal.gerencias.length > 0);

  if (status === "error" || (status === "success" && !hasData)) {
    return (
      <main className="min-h-screen bg-white">
        <Hero t={t} />
        <div className="max-w-2xl mx-auto py-24 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            {t("errorTitle")}
          </h2>
          <p className="text-gray-600 text-base">{t("errorMessage")}</p>
        </div>
      </main>
    );
  }

  const { gerente, vicepresidencias, gerencias } = personal || {
    gerente: [],
    vicepresidencias: [],
    gerencias: [],
  };

  return (
    <main className="min-h-screen bg-white">
      <Hero t={t} />

      {status === "loading" ? (
        <div className="max-w-[80%] mx-auto py-16">
          <OrganizacionSkeleton />
        </div>
      ) : (
        <section className="w-full max-w-[80%] mx-auto py-24">
          <SectionBlock title={t("gerenteTitle")} people={gerente} featured />
          <SectionBlock
            title={t("vicepresidenciasTitle")}
            people={vicepresidencias}
          />
          <SectionBlock title={t("gerenciasTitle")} people={gerencias} />
        </section>
      )}
    </main>
  );
}
